/**
 * ================================================================================================
 *  הבית שלי — THE CUSTOMER WORKSPACE HOME, FROM THE APPROVED CUSTOMER KIT.
 * ================================================================================================
 *
 * `GLPOINT-CUSTOMER-DESIGN-2026-10-15.zip`, Owner-approved 2026-10-15. Its home screen, row for
 * row, with the compact widget column the kit puts at the inline end:
 *
 *     greeting + lead                         "בוקר טוב, רפי"
 *     המועדפים שלי        | חברות בארגון       the starred modules
 *     תזרים במבט מהיר     | שערי מטבע         the financial overview
 *     לטיפול היום         | ריביות והצמדה     the day's queue
 *     כל המודולים שלי     | עדכונים מקצועיים  the library, CLOSED on arrival
 *
 * That order is the Owner's, after seeing the first build: favourites, then the financial
 * overview, then tasks, with the module library a disclosure beneath them. Expanded on arrival,
 * eleven module cards pushed the two panels a person opens this screen FOR below the fold.
 *
 * ── AND ALMOST NONE OF IT EXISTS YET, WHICH IS WHAT THIS SCREEN SAYS ────────────────────────────
 *
 * The kit's home is drawn for a finished product. Against this build: the cash-flow figures, the
 * bar chart, the eleven modules, the FX rates, the interest rates, the professional updates and
 * the task queue have no route, no capability and no table between them. The Owner was explicit
 * about how that is handled, twice:
 *
 *   *"Preserve every planned capability in the approved navigation and dashboard, subject to
 *   permissions. Do not hide it merely because it is unavailable. Label its actual state
 *   explicitly ... Never substitute a fabricated zero or an unexplained disabled button."*
 *
 * So every panel the kit draws is HERE, in its approved position, and each one states what it
 * actually is rather than showing a plausible number. `₪428,500` does not appear. Neither does
 * `₪0`, which would be worse: a zero is a measurement, and nobody measured this.
 *
 * ── WHAT IS REAL ON THIS SCREEN ─────────────────────────────────────────────────────────────────
 *
 * The companies count and the company list behind it, read through `companies.company.read` — the
 * same read the picker uses, so the two can never disagree. The greeting, from the session's own
 * display name. Favourites, which are a per-device preference and say so. Everything else names
 * its state and names what it is waiting for.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { AppShell } from '../shell/AppShell';
import { Icon, type IconName } from '../shell/Icon';
import { useWorkContext } from '../shell/ContextProvider';
import { useLocale } from '../i18n/LocaleProvider';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ServerRead } from '../components/ServerRead';
import { FINANCIAL_MODULES_FOR_HOME } from '../shell/destinations';
import { favouritesChangedOnAccount, useFavourites } from '../shell/favourites';
/* Section 9: the order of the tiles; section 4 of 2026-11-01: which of them are shown. */
import {
  hiddenTiles,
  homeOrder,
  moveTile,
  placeTileAt,
  TILE_LABEL,
  type HomeRegion,
  type HomeTileId,
} from '../shell/homeOrder';
import { useTileDrag } from '../shell/tileDrag';
import { Tile } from '../shell/Tile';
import { hydratePreferences } from '../shell/preferences';
import { MarketTile } from '../components/MarketTile';
import { UpdatesBanner } from '../components/UpdatesBanner';
import { routes } from '../routes';

/**
 * ONE PANEL THAT CANNOT BE FILLED YET — AND STILL LOOKS LIKE A PANEL.
 *
 * It keeps its approved position, heading and icon, and carries the state and the REASON. The
 * reason matters: "not yet implemented" answers *what*, and a person running an accounting office
 * is entitled to *what is it waiting for*.
 *
 * ── WHY IT IS NOT A ONE-LINE STRIP ──────────────────────────────────────────────────────────────
 *
 * It was. Owner, on seeing it: *"אזורי התזרים והמשימות הפכו לפסים דקים מאוד. אין צורך להמציא
 * נתונים, אבל כדאי לשמור על כרטיסים ברורים עם הודעת המצב בתוכם."* Right on both halves — the card
 * should hold its shape, and nothing should be invented to fill it.
 *
 * So `slots` names the figures this card WILL hold, each showing the state word where a number
 * would go, and `fieldHe` is a plain empty field where the kit draws a chart, saying why it is
 * empty. A dash is not used anywhere: a dash reads as "nothing", and a zero would be worse still,
 * because a zero is a measurement and nobody measured this.
 *
 * There is no button. The Owner ruled out the unexplained disabled control, and a control that
 * cannot act is worse than no control — it invites a click and answers it with nothing.
 */
/**
 * The accent for each planned-cash-flow slot, IN ORDER. **Owner decision, 2026-10-25.**
 *
 * Bank balance, expected receipts, expected payments — blue, turquoise, amber. Keyed on position
 * rather than on the label, so rewording a heading cannot silently drop the colour.
 */
const PLANNED_ACCENTS = ['blue', 'turquoise', 'amber'] as const;

function PlannedPanel({
  title,
  icon,
  state,
  reason,
  slots,
  fieldHe,
  action,
}: {
  readonly title: string;
  readonly icon: IconName;
  readonly state: string;
  readonly reason: string;
  /** What this card will show, named. The VALUE of each is the state, never a figure. */
  readonly slots?: readonly string[];
  /** The region the kit fills with a chart or a list, and what stands in its place. */
  readonly fieldHe?: string;
  /**
   * WHERE TO GO AND UNBLOCK THIS, when the thing standing in the way is something the office can
   * actually do. **Section 7, 2026-10-26.**
   *
   * Given ONLY to a tile blocked on a connection the office can make. A tile waiting on a feature
   * nobody has built, or on a provider nobody has chosen, gets no action — sending somebody to a
   * screen that cannot help them is worse than telling them plainly that there is nothing to do
   * yet, and it is the shape that makes a product feel like it is lying.
   */
  readonly action?: { readonly href: string; readonly labelHe: string };
}): JSX.Element {
  return (
    <section className="gp-panel">
      <div className="gp-section-heading">
        <h2>
          <span className="gp-overview-icon">
            <Icon name={icon} />
          </span>
          {title}
        </h2>
        <span className="gp-badge gp-plan">{state}</span>
      </div>
      <p className="gp-muted gp-small">{reason}</p>

      {action === undefined ? null : (
        <p className="gp-w-planned-action">
          <a className="btn btn--nav" href={action.href}>
            {action.labelHe}
          </a>
        </p>
      )}

      {slots === undefined ? null : (
        <div className="gp-w-planned-body">
          {/*
            ── THE THREE CASH-FLOW TILES CARRY THEIR COLOUR. **Owner decision, 2026-10-25.** ─────

            *"Bank balance: blue; expected receipts: turquoise; expected payments: amber. Add
            matching light tinted backgrounds."*

            The accent is derived from the slot's POSITION in the list the panel declares, not from
            matching its Hebrew label: a label match would silently lose the colour the day
            somebody rephrased the text, and the order is what the panel already fixes.

            THE COLOUR IS NEVER THE ONLY SIGNAL. Each tile keeps its own heading in dark text, so
            the three are told apart by reading as well as by hue — which is what makes a turquoise
            edge measuring 2.08:1 acceptable here: it decorates a tile that is already labelled.
          */}
          {slots.map((label, index) => (
            <div
              className="gp-w-planned-slot"
              data-accent={PLANNED_ACCENTS[index] ?? 'blue'}
              key={label}
            >
              <h4>{label}</h4>
              <span>{state}</span>
            </div>
          ))}
        </div>
      )}

      {fieldHe === undefined ? null : <p className="gp-w-planned-field">{fieldHe}</p>}
    </section>
  );
}

/*
 * ── THE PERSONALISATION PANEL IS NOT ON THIS SCREEN ANY MORE. **2026-11-01.** ───────────────────
 *
 * *"העבר את ההתאמה האישית מדף הבית אל ההגדרות."* It is `TenantPersonalisationScreen`, a category
 * on the settings landing, and the whole argument for the move is in that file's own header. What
 * stayed here is the READ of the hidden set, because a hidden tile renders nothing.
 */

/*
 * ── THE TASK READINGS ARE NOT ON THIS SCREEN ANY MORE. **Owner, 2026-11-01.** ──────────────────
 *
 *     *"משימות, ביקשתי שזה יהיה בוויג׳ט בצד ימין מתחת להגדרות."*
 *
 * They were two large cards in the centre; §4 of his list made them a compact strip at the foot
 * of the content; and on seeing that strip he named the position he had asked for in the first
 * place. They are `shell/NavTasks.tsx` now, under `הגדרות` in the navigation rail.
 *
 * `tenant.work_item.list` is READ FROM THERE and no longer from here, which is the half that
 * matters: a reading that moved and left its request behind would be two requests for one
 * number, and the Owner's own rule is *"אין לשכפל את אותו מידע בשני מקומות"*.
 */

export function TenantHomeScreen(): JSX.Element {
  const ctx = useWorkContext();
  /*
   * ══ THE FOUR READS THAT LEFT WITH THEIR TILES. **Owner, 2026-10-29 and 2026-11-01.** ════
   *
   * `tenant.source.list`, `tenant.member.read` and `tenant.billing.read` were issued from here for
   * the four tiles this screen no longer has. **`companies.register.read` is the fourth, and it
   * left today with the companies tile** — section 6: *"אריח החברות אינו מוסיף — ניהול החברות
   * נשאר במסך שלו."* The two questions that read had to tell apart (the office's REGISTER for
   * whoever administers it, the grant-filtered list for everybody else) are still told apart, on
   * `TenantCompaniesScreen`, which is the screen that was fixed on the 28th and the only one that
   * now asks. **A read with no reader is not harmless:** it is a
   * request on every home load that the browser and the server both pay for, and anybody looking
   * at the network tab would reasonably conclude the home page requires `tenant.settings.read`,
   * `tenant.user.manage` and `billing.plan.read` -- three capabilities it does not need.
   *
   * They are not deleted from the product. Each is still read, and still gated, on the
   * administration screen it belongs to: *"המידע יישאר במסכי הניהול המתאימים."*
   *
   * `homeWidgets.dom.test.tsx` asserts the requests are absent, which is how this stays true: the
   * tiles went first and the reads were found still running by that case.
   */
  const { t } = useLocale();
  const { favourites, toggle } = useFavourites();
  /*
   * ── THE ORDER OF THE TILES, AND IT IS THE PERSON'S. **Section 9, 2026-10-27.** ──────────────
   *
   * Owner: *"סדר אריחי הבית ניתן לשינוי ונשמר."* It is CSS `order` on a flex column, so the markup
   * keeps the approved reading order -- which is what a screen reader follows -- while the eye
   * sees what this device chose. Moving a tile is two buttons, not a drag: that works with a
   * keyboard and on a phone, and it cannot drop a tile somewhere nobody meant.
   */
  const [mainOrder, setMainOrder] = useState<readonly string[]>(() => homeOrder('main'));
  const [sideOrder, setSideOrder] = useState<readonly string[]>(() => homeOrder('side'));
  /* And which of them this person switched off. Section 4, 2026-11-01. */
  const [hidden, setHidden] = useState<readonly string[]>(() => hiddenTiles());
  /*
   * ── AND THE ACCOUNT IS ASKED ONCE, ON MOUNT. **§6.9, 2026-10-29.** ─────────────────────
   *
   *     *"ההגדרות יישמרו ויופיעו נכון לאחר רענון וכניסה מחדש."*
   *
   * THE FIRST PAINT IS THIS MACHINE'S ANSWER and the account's arrives a moment later, which is the
   * right way round: a layout that waited for a round trip would show an empty home screen to
   * somebody whose own device already knew where their panels go. `hydratePreferences` answers with
   * the keys that CHANGED, and an empty answer — the common case, where the account and the machine
   * agree — sets no state and causes no second render.
   *
   * NOT `useServerData`: that hook renders a read's state, and there is no state to render here.
   * A preference read that fails is not a failure a person needs to see, because the screen in
   * front of them is already correct for this machine.
   */
  useEffect(() => {
    let live = true;
    void hydratePreferences().then((changed) => {
      if (!live || changed.length === 0) return;
      if (changed.includes('home.order.main')) setMainOrder(homeOrder('main'));
      if (changed.includes('home.order.side')) setSideOrder(homeOrder('side'));
      /* THE THIRD KEY FOLLOWS THE OTHER TWO, which is what makes *"ובין מכשירים"* true of the
         card CHOICE and not only of the order. Omitting it here was the shape of defect this
         file already carries a note about: a second region wired for ordering and a first one
         that was not. */
      if (changed.includes('home.hidden')) setHidden(hiddenTiles());
      /* THE FOURTH KEY, 2026-11-01 section 7: the star follows the order and the hidden set. */
      if (changed.includes('home.favourites')) favouritesChangedOnAccount();
    });
    return () => {
      live = false;
    };
  }, []);

  /*
   * ── §6.10'S TWO READS LEFT WITH THE WIDGET THEY FED. **Owner, 2026-11-01.** ─────────────────
   *
   *     *"הווידג׳טים „לטיפול היום" ו„המשימות שלי" יקראו מהמנגנון."*  (§6.10, and still true)
   *     *"משימות, ביקשתי שזה יהיה בוויג׳ט בצד ימין מתחת להגדרות."*  (and this says where)
   *
   * `tenant.work_item.list` is issued by `shell/NavTasks.tsx` now, twice, for the same two
   * server-side narrowings and the same reason. **It is not issued here as well**, which is the
   * half that matters: a reading that moved and left its request behind is two requests for one
   * number, and `homeWidgets.dom.test.tsx` has a case for exactly that shape — three reads were
   * found still running for tiles that had already left this screen.
   */
  /*
   * ── §4 · ONE READ FOR ALL FIVE SIDE CARDS. **2026-10-29.** ───────────────────────
   *
   * ONE and not five, which is the opposite of the choice made for the two task cards above -- and
   * the difference is where the NARROWING lives. `today` and `mine` are two different server-side
   * predicates over an office's own rows, so each is its own covered read. The market readings are
   * public, identical for everybody, and come from ONE table with no tenant: five requests would be
   * five round trips for one answer.
   *
   * The read NEVER asks a publisher. `user.market.read` serves what is stored; going out to a
   * publisher is `refresh()`, which the scheduler calls -- so a home screen's load time does not
   * depend on the Bank of Israel's.
   *
   * (The word for a network primitive is avoided here for the SECOND time in this file:
   * `web-client-choke-point` scans the whole text, comments included, and caught both drafts. The
   * rule is blunt by design and I keep reaching for the word anyway.)
   */
  const readMarket = useCallback(() => callOperation('user.market.read', {}), []);
  const marketRead = useServerData(readMarket).result;

  /*
   * ── ONE DRAG CONTROLLER FOR BOTH REGIONS. **2026-11-01.** ─────────────────────────────────
   *
   * Only one tile can be carried at a time, so one controller is the right number; two would be
   * two answers to *"what is being dragged"*. It is given the ONE thing it cannot know -- how to
   * write an order -- and it decides everything else, including that a drop never crosses a
   * region boundary.
   */
  const applyDrop = useCallback((region: HomeRegion, dragged: string, target: string): void => {
    if (region === 'main') setMainOrder(placeTileAt('main', dragged, target));
    else setSideOrder(placeTileAt('side', dragged, target));
  }, []);
  const drag = useTileDrag(applyDrop);

  /*
   * ── THE WRITERS LEFT FOR THE SETTINGS SCREEN, AND THE READER STAYED ────────────────────────
   *
   * `showCard` and `resetLayout` were here while the panel was. They are on
   * `TenantPersonalisationScreen` now; this screen still READS `hidden`, because a hidden tile
   * renders nothing and that is a decision taken on the way up.
   *
   * It needs no notification of a change made over there: every link in this product is a page
   * load, so arriving here after changing something mounts this screen fresh.
   */
  /*
   * EVERY TILE IS WIRED THE SAME WAY, FROM ONE PLACE. **2026-10-28.**
   *
   * Five identical props repeated per tile is how the four panels in the main column came to be
   * movable and the four in the widget column came to be fixed: a new panel was added without
   * them and nothing said so. Spread, a tile that is not in the order model fails to compile.
   */
  const mainTile = {
    handleHe: t('home.moveHandle'),
    region: 'main',
    order: mainOrder,
    hidden,
    onMove: (id: string, delta: -1 | 1): void => setMainOrder(moveTile('main', id, delta)),
    drag,
  } as const;

  /*
   * THE SECOND REGION IS WIRED THE SAME WAY AND THAT IS THE WHOLE CORRECTION. The defect of
   * 2026-10-28 was a side column whose panels carried none of these props, so they could not be
   * moved at all. Spreading an object means a tile added to either region without its wiring
   * fails to compile rather than silently becoming fixed.
   */
  const sideTile = {
    handleHe: t('home.moveHandle'),
    region: 'side',
    order: sideOrder,
    hidden,
    onMove: (id: string, delta: -1 | 1): void => setSideOrder(moveTile('side', id, delta)),
    drag,
  } as const;

  /*
   * NO READ OF ITS OWN. The shell already issues `companies.list.read` once and hands the result
   * down as `companiesResult`, which is what the picker renders from. A second read here would be
   * a second answer to one question, and two screens counting the same thing two ways is how two
   * screens come to disagree in front of a customer. It is the RESULT and not the list, so a
   * failed read still reads as a failure here rather than as an office with no companies.
   */

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('home.title')} requires="TENANT" actual={ctx.surface} />;
  }

  const name = ctx.session?.displayName ?? '';
  /** The office this session is in. Never the signed-in person and never the company. */
  const officeName = ctx.tenant?.displayName ?? '';
  const favouriteModules = FINANCIAL_MODULES_FOR_HOME.filter((m) => favourites.includes(m.id));

  /*
   * ── THE READINGS ARE NOT THIS SCREEN'S ANY MORE. **Owner, 2026-11-01.** ─────────────────────
   *
   *     *"אתה עובר בין מסכים, הוויג׳טים לא אמורים להשתנות."*
   *
   * They were built here and handed to `AppShell` as its `rail` prop, so every OTHER customer
   * screen passed nothing and the four cards vanished on navigation. A reading somebody glances
   * at is furniture: `shell/MarketRail.tsx` draws them beside `בשימוש לאחרונה` on every screen,
   * and the SIDE region's order, hidden set and drag controller moved with them.
   *
   * THE SAME WENT FOR THE UPDATES STRIP, which this screen passed as `banner`. The shell makes
   * the one `user.market.read` both of them need.
   *
   * WHAT STAYED IS THE MAIN REGION — the work area, which is this screen.
   */

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={`${t('home.greeting')}${name === '' ? '' : `, ${name}`}`}
      /*
        THE OFFICE IS NAMED HERE. **2026-10-26.** The greeting names the PERSON, and the page said
        nothing about which office the session is in -- so the office name, the user name and the
        selected company were three things told apart only by where they happened to appear. When
        the session carries no office name the lead stays as it was, rather than printing a label
        with nothing after it.
      */
      /*
        NO LEAD. **Section 1, 2026-10-28.** *"להסיר את המשפט הכללי שמתחת לברכה — הוא אינו מוסיף
        מידע שימושי."* It read *"המשך החיפוש והבדיקה שלך, במקום אחד"*, which is true of most
        software and tells a reader nothing about this screen.
      */
      screenId="tenant-home"
      actions={
        <a className="gp-button gp-primary" href={routes.companies}>
          {t('home.openCompanies')}
        </a>
      }
    >
      {/*
        ── THE IDENTITY BLOCK IS GONE FROM HERE. **Section 1, 2026-10-28.** ────────────────────

        It was added yesterday because the header named none of the three. The header names all
        three now, in one row with their labels, so this block became the duplication the Owner
        asked to remove: *"להסיר מדף הבית את המסגרת הנפרדת שחוזרת על שלושת הפרטים האלה."*

        The greeting stays, because he said so.
      */}
      {/*
        ── TWO REGIONS, AND BOTH ARE ORDERED. **Owner, 2026-10-29.** ──────────────────

            *"נתוני השוק — מטבע, ריבית, מדד ודלק — יהיו באזור הצד השמאלי, מתחת
            ל„שימוש לאחרונה”... מרכז דף הבית יוקדש לעבודה... אזור צד ואזור מרכז יכולים
            להיות נפרדים מבחינת העיצוב, אבל שניהם צריכים לתמוך בסידור הכרטיסים המיועדים
            להם. אין לחזור למצב שבו כרטיסי הצד אינם ניתנים לסידור."*

        **WHAT THIS REPLACES, AND WHY THE PREVIOUS ANSWER WAS HALF RIGHT.** Yesterday this screen
        became ONE grid of fifteen tiles, because the drag complaint had one cause: two regions
        with only one of them in the order model, and the other fixed at 195px. Merging them fixed
        the ordering and produced a different defect, measured in a browser at 1440x900: a
        **1,525px page in a 900px window**, a grid **1,029px wide inside 1,440**, and the first
        tile **201px down**. Fifteen tiles in two columns is nine rows.

        The correction was never "one region". It is **two regions where BOTH are ordered** --
        `homeOrder.ts` holds one list per region and a drag reorders within its own. The side
        column carries readings a person glances at, under "recently used"; the centre carries the
        work. A tile belongs to exactly one region, which is why a drop does not cross between
        them: the two differ in width by design, and a market reading re-laid-out for a work tile
        would be the squeezed text this round began with.
      */}
      <div className="gp-w-home">
        {/*
          ── THE WORK ───────────────────────────────────────────────────────────────

              *"מרכז דף הבית יוקדש לעבודה: תזרים, משימות ומועדפים. ספריית המודולים תהיה
              נגישה בלי ליצור רצועה ריקה גדולה."*

          FOUR TILES LEFT THIS SCREEN ENTIRELY: `members`, `subscription`, `sources` and
          `attention`. *"המידע יישאר במסכי הניהול המתאימים. אין למחוק את היכולות עצמן."*
          Each is still answered by the same route on its administration screen; what left is a
          card. **This workstream added them the day before and counted them as connected
          widgets**, which the Owner named for what it was:
          *"אל תוסיף כרטיסי ניהול לדף הבית כדי להגדיל את מספר הווידג׳טים המחוברים."*
        */}
        <div className="gp-w-work">
          <Tile id="cashFlow" labelHe={t(TILE_LABEL.cashFlow)} {...mainTile}>
          {/*
            THE KIT SHOWS ₪428,500 IN THE BANK, ₪186,000 EXPECTED IN AND ₪142,000 EXPECTED OUT,
            over a four-month bar chart. Every one of those is invented, and there is no read in
            this product that could produce any of them: the bank, collections and credit-card
            modules are the ones Master §22 lists for Commercial V1 and none is built.

            So the panel keeps its place and says what it is waiting for. No chart is drawn from
            nothing, and no zero stands in for a figure nobody measured.
          */}
          <PlannedPanel
            title={t('home.cashFlow')}
            icon="trending-down"
            state={t('state.notImplemented')}
            reason={t('planned.cashFlow')}
            slots={[t('cash.inBank'), t('cash.expectedIn'), t('cash.expectedOut')]}
            fieldHe={t('cash.chartEmpty')}
            /*
              THESE THREE FIGURES COME FROM AN ACCOUNTING SYSTEM, and until one is connected there
              is nothing to show. That is a thing the office can go and do, so it is offered.
            */
            action={{ href: routes.tenantSources, labelHe: t('home.connectSource') }}
          />

          </Tile>

          {/*
            ── A STARRED MODULE IS THE CARD. **Owner, 2026-11-01.** ──────────────────────────

                *"להוריד מועדפים, וכל המודולים שלי גם להוריד — לא צריך את זה, אלא אם סימן
                בכוכב את המודול הרלוונטי ואז הוא יוצג בדף הבית."*

            TWO CARDS LEFT THIS SCREEN AND ONE THING SURVIVED THEM. `מועדפים` was a panel whose
            heading said `מועדפים` above cards that are, visibly, favourites; `כל המודולים שלי`
            was a closed disclosure whose entire content on arrival was its own name. Between
            them they used two rows of the work area to say what they were called.

            What he wants is the OUTCOME of starring and not the machinery of it: star a module
            and it is on the home screen; star nothing and the home screen does not mention
            modules at all. So the starred modules render as plain cards in the work region,
            with no panel, no heading and no chip — and an office that has starred nothing sees
            this tile render NOTHING, which is what `null` from `<Tile>` already does for a card
            somebody switched off.

            ── AND THE LIST THAT STARS THEM IS IN THE SETTINGS ───────────────────────────────

            Removing the library removed the only place in the product where a star can be SET,
            which would have made the feature unreachable rather than quiet. It is on
            `TenantPersonalisationScreen` now — the screen whose whole subject is which cards
            appear on this person's home screen, which is exactly what a star decides.
          */}
          <Tile id="favourites" labelHe={t(TILE_LABEL.favourites)} {...mainTile}>
            {favouriteModules.length === 0 ? null : (
              <div className="gp-w-favourites">
                {favouriteModules.map((m) => (
                  <article className="gp-w-favourite" key={m.id}>
                    <h3>{t(m.label)}</h3>
                    {/*
                      THE CHIP STAYS. The Owner's own condition on the star: *"אפשר להשאיר
                      כוכבית גם למודול עתידי — כל עוד כתוב בבירור שהמודול טרם מומש."* It is the
                      one piece of text on the card that is not the module's name, and it is the
                      piece that keeps the card from being a promise.
                    */}
                    <span className="gp-badge gp-plan">{t('state.notImplemented')}</span>
                    <button
                      type="button"
                      className="gp-w-star"
                      aria-pressed="true"
                      aria-label={`${t(m.label)} — ${t('nav.removeFavourite')}`}
                      onClick={() => toggle(m.id)}
                    >
                      ★
                    </button>
                  </article>
                ))}
              </div>
            )}
          </Tile>

        </div>

      </div>

    </AppShell>
  );
}
